import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import {
  useApplyRoleRule,
  useCreateRoleRule,
  useDeleteRoleRule,
  useRoleRules,
  useTestRoleRule,
  useUpdateRoleRule,
} from '../../hooks/use-admin-role-rules.js';
import type { CreateRuleBody, RuleApplyResponse, RuleCondition } from '../../lib/admin-client.js';

const KNOWN_FIELDS = [
  '$user.is_admin',
  '$user.is_default',
  '$user.email',
  '$user.display_name',
  '$user.attributes.',
];

const OPS = ['eq', 'neq', 'in', 'starts_with', 'ends_with', 'contains'] as const;

const s = {
  container: { display: 'flex', flexDirection: 'column' as const, gap: '1rem' },
  card: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.card,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  row: { display: 'flex', gap: '0.5rem', alignItems: 'flex-start' },
  input: {
    backgroundColor: '#1f2937',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.375rem 0.625rem',
    fontSize: '0.8125rem',
    outline: 'none',
    fontFamily: 'inherit',
    flex: 1,
  },
  select: {
    backgroundColor: '#1f2937',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.375rem 0.625rem',
    fontSize: '0.8125rem',
    outline: 'none',
  },
  btn: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.375rem 0.75rem',
    fontSize: '0.8125rem',
    cursor: 'pointer',
  },
  btnSm: {
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    color: tokens.colors.textMutedDark,
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnDanger: {
    backgroundColor: 'transparent',
    border: `1px solid ${tokens.colors.error}`,
    borderRadius: tokens.radius.button,
    color: tokens.colors.error,
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  ruleMeta: { fontSize: '0.8125rem', color: tokens.colors.textMutedDark },
};

interface RulesTabProps {
  tenantId: string;
  roleId: string;
}

// N-9: convert condition value to the right shape before submitting
function coerceConditionValue(op: (typeof OPS)[number], raw: string): unknown {
  if (op === 'in') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return raw;
}

// N-9: display existing value sensibly regardless of type
function displayValue(op: (typeof OPS)[number], value: unknown): string {
  if (op === 'in' && Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value ?? '');
}

function RuleBuilder({
  initial,
  onSave,
  onCancel,
}: {
  initial?: { name: string; condition: RuleCondition; enabled: boolean };
  onSave: (b: CreateRuleBody) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [field, setField] = useState(initial?.condition.field ?? '$user.email');
  const [op, setOp] = useState<(typeof OPS)[number]>(initial?.condition.op ?? 'eq');
  const [value, setValue] = useState(
    displayValue(initial?.condition.op ?? 'eq', initial?.condition.value ?? ''),
  );
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);

  return (
    <div style={s.card}>
      <div style={s.row}>
        <input
          style={s.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Rule name"
          aria-label="Rule name"
        />
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            color: tokens.colors.textMutedDark,
            fontSize: '0.875rem',
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            aria-label="Rule enabled"
          />
          Enabled
        </label>
      </div>
      <div style={s.row}>
        <select
          style={s.select}
          value={field}
          onChange={(e) => setField(e.target.value)}
          aria-label="Condition field"
        >
          {KNOWN_FIELDS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <select
          style={s.select}
          value={op}
          onChange={(e) => setOp(e.target.value as typeof op)}
          aria-label="Condition operator"
        >
          {OPS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {/* N-9: when op is 'in', hint that comma-separated values are expected */}
        <input
          style={s.input}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={op === 'in' ? 'val1, val2, val3' : 'value'}
          aria-label={op === 'in' ? 'Condition values (comma-separated)' : 'Condition value'}
        />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          type="button"
          style={s.btn}
          onClick={() =>
            onSave({
              name,
              condition: { field, op, value: coerceConditionValue(op, value) },
              enabled,
            })
          }
          disabled={name.trim().length === 0}
          aria-label="Save rule"
        >
          Save
        </button>
        <button
          type="button"
          style={{
            ...s.btn,
            backgroundColor: 'transparent',
            border: `1px solid ${tokens.colors.borderDark}`,
            color: tokens.colors.textMutedDark,
          }}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function RulesTab({ tenantId, roleId }: RulesTabProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [testJson, setTestJson] = useState('');
  const [testRuleId, setTestRuleId] = useState('');
  const [testResult, setTestResult] = useState<boolean | null>(null);
  // W-5: track apply results per rule to avoid a shared global success state
  const [appliedResults, setAppliedResults] = useState<Map<string, RuleApplyResponse>>(new Map());

  const { data, isLoading } = useRoleRules(tenantId, roleId);
  const createRule = useCreateRoleRule(tenantId, roleId);
  const updateRule = useUpdateRoleRule(tenantId, roleId);
  const deleteRule = useDeleteRoleRule(tenantId, roleId);
  const testRule = useTestRoleRule(tenantId);
  const applyRule = useApplyRoleRule(tenantId);

  function handleTest(ruleId: string) {
    setTestRuleId(ruleId);
    setTestJson('');
    setTestResult(null);
  }

  function handleRunTest() {
    let attrs: Record<string, unknown> = {};
    try {
      attrs = JSON.parse(testJson) as Record<string, unknown>;
    } catch {
      return;
    }
    const rule = data?.items.find((r) => r.id === testRuleId);
    if (!rule) return;
    testRule.mutate(
      { rule: rule.condition as RuleCondition, user_attributes: attrs },
      { onSuccess: (res) => setTestResult(res.matches) },
    );
  }

  const rules = data?.items ?? [];

  return (
    <div style={s.container}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          style={s.btn}
          onClick={() => setShowCreate(true)}
          aria-label="Add rule"
        >
          + Add Rule
        </button>
      </div>

      {showCreate && (
        <RuleBuilder
          onSave={(body) => createRule.mutate(body, { onSuccess: () => setShowCreate(false) })}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {isLoading ? (
        <output
          style={{ display: 'block', color: tokens.colors.textMutedDark, fontSize: '0.875rem' }}
          aria-label="Loading rules"
        >
          Loading…
        </output>
      ) : (
        rules.map((rule) => (
          <div key={rule.id} style={s.card}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <div>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    color: tokens.colors.textDark,
                    fontSize: '0.875rem',
                  }}
                >
                  {rule.name}
                </p>
                <p style={s.ruleMeta}>
                  {rule.condition.field} {rule.condition.op}{' '}
                  <code style={{ fontFamily: 'monospace', color: '#86efac' }}>
                    {String(rule.condition.value)}
                  </code>
                  {' · '}
                  {rule.enabled ? 'enabled' : 'disabled'}
                  {rule.matched_count !== undefined && ` · ${rule.matched_count} matched`}
                </p>
              </div>
              <div>
                <button
                  type="button"
                  style={s.btnSm}
                  onClick={() => handleTest(rule.id)}
                  aria-label={`Test rule ${rule.name}`}
                >
                  Test
                </button>
                <button
                  type="button"
                  style={s.btnSm}
                  onClick={() =>
                    updateRule.mutate({ ruleId: rule.id, body: { enabled: !rule.enabled } })
                  }
                  aria-label={rule.enabled ? 'Disable rule' : 'Enable rule'}
                >
                  {rule.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  type="button"
                  style={s.btnSm}
                  onClick={() =>
                    applyRule.mutate(rule.id, {
                      // W-5: store result per ruleId, not in shared mutation state
                      onSuccess: (res) =>
                        setAppliedResults((prev) => new Map(prev).set(rule.id, res)),
                    })
                  }
                  disabled={applyRule.isPending}
                  aria-label={`Apply rule ${rule.name}`}
                >
                  Apply
                </button>
                <button
                  type="button"
                  style={s.btnDanger}
                  onClick={() => deleteRule.mutate(rule.id)}
                  aria-label={`Delete rule ${rule.name}`}
                >
                  Delete
                </button>
              </div>
            </div>

            {appliedResults.has(rule.id) && (
              <output style={{ display: 'block', color: '#86efac', fontSize: '0.8125rem' }}>
                Applied: +{appliedResults.get(rule.id)?.added} / -
                {appliedResults.get(rule.id)?.removed}
              </output>
            )}

            {testRuleId === rule.id && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <textarea
                  style={{
                    ...s.input,
                    minHeight: '60px',
                    resize: 'vertical',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box',
                    width: '100%',
                  }}
                  value={testJson}
                  onChange={(e) => setTestJson(e.target.value)}
                  placeholder='{"department": "Ventas", ...}'
                  aria-label="User attributes JSON for test"
                />
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    type="button"
                    style={s.btn}
                    onClick={handleRunTest}
                    disabled={testRule.isPending}
                    aria-label="Run test"
                  >
                    {testRule.isPending ? 'Testing…' : 'Run Test'}
                  </button>
                  {testResult !== null && (
                    <span
                      style={{ fontSize: '0.875rem', color: testResult ? '#86efac' : '#fca5a5' }}
                    >
                      {testResult ? '✓ Matches' : '✗ No match'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))
      )}
      {!isLoading && rules.length === 0 && (
        <p
          style={{ color: tokens.colors.textMutedDark, fontSize: '0.875rem', textAlign: 'center' }}
        >
          No rules configured
        </p>
      )}
    </div>
  );
}
